import { useTranslation } from '@cobliteam/i18n';
import { Tooltip, Typography } from '@cobliteam/ui/components';
import { cn } from '@cobliteam/ui/utils';
import { type CSSProperties, forwardRef, type ReactNode, useState } from 'react';
import { tv } from 'tailwind-variants';
import AIIcon from '../AIIcon';

/** `solid`: full gradient bg + white sparkles. `soft`: frosted gradient bg + gradient sparkles. `ghost`: no background. `ai-outline`: white bg + gradient border. */
export type AIButtonVariant = 'solid' | 'soft' | 'ghost' | 'ai-outline';

export type AIButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/** `rounded`/`circle`: icon-only. `cta`: wide button with sparkles + label. */
export type AIButtonShape = 'rounded' | 'circle' | 'cta';

export type AIButtonProps = {
  showTooltip?: boolean;
  className?: string;
  dataTestId?: string;
  /** Native `data-testid`. Takes precedence over {@link AIButtonProps.dataTestId} when both are set. */
  'data-testid'?: string;
  /** Overrides the default accessible name (derived from `label` / i18n). */
  'aria-label'?: string;
  disabled?: boolean;
  size?: AIButtonSize;
  style?: CSSProperties;
  /** Visual style. @default 'solid' */
  variant?: AIButtonVariant;
  /** Button shape / layout mode. @default 'rounded' */
  shape?: AIButtonShape;
  /** When set, renders this icon element (e.g. `<Icon name="..." size="sm" color="..." />` from `@cobliteam/ui/icons`) instead of the animated AI sparkles. Caller controls size/color. */
  icon?: ReactNode;
  /** Text displayed when shape === 'cta' */
  label?: ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
};

const ICON_SIZE_MAP: Record<AIButtonSize, number> = { xs: 22, sm: 32, md: 38, lg: 42, xl: 46, '2xl': 58 };

/** Sparkle px inside a `cta` button, per size. */
const CTA_ICON_SIZE_MAP: Record<AIButtonSize, number> = { xs: 18, sm: 22, md: 32, lg: 36, xl: 40, '2xl': 46 };

/** Label typography variant inside a `cta` button, per size. */
const CTA_LABEL_VARIANT: Record<AIButtonSize, 'text-sm-medium' | 'text-md-medium'> = {
  xs: 'text-sm-medium',
  sm: 'text-md-medium',
  md: 'text-md-medium',
  lg: 'text-md-medium',
  xl: 'text-md-medium',
  '2xl': 'text-md-medium',
};

// Cobli AI gradients — Figma "Cobli IA Colors" (node 707:2887).
/** Background gradient — solid fills (Contained primary). */
const AI_BACKGROUND_GRADIENT = 'linear-gradient(248deg, #8b5cf6 20%, #0248e9 60%, #001bb8 100%)';
/** Foreground gradient — text, borders and sparkles (also used as the `from/to` tailwind stops below). */
const AI_FOREGROUND_GRADIENT = 'linear-gradient(90deg, #7c3aed 0%, #6d28d9 15%, #0248e9 65%, #327af6 100%)';

/** Frosted overlay — used for `soft` variant (Contained secondary). */
const SOFT_BACKGROUND_STYLE = {
  backgroundImage: `linear-gradient(90deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), ${AI_BACKGROUND_GRADIENT}`,
} as const;

/** White fill + gradient border via border-box clip — used for `ai-outline` (Border). */
const AI_OUTLINE_BACKGROUND_STYLE = {
  border: '1px solid transparent',
  backgroundImage: `linear-gradient(#fff, #fff), ${AI_FOREGROUND_GRADIENT}`,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
} as const;

function getVariantStyle(variant: AIButtonVariant, style?: CSSProperties): CSSProperties | undefined {
  if (variant === 'soft') return { ...SOFT_BACKGROUND_STYLE, ...style };
  if (variant === 'ai-outline') return { ...AI_OUTLINE_BACKGROUND_STYLE, ...style };
  return style;
}

// ─── CTA (shape === 'cta') ───────────────────────────────────────────────────

const aiButtonCtaStyles = tv({
  slots: {
    button:
      'group relative inline-flex pointer-events-auto shrink-0 cursor-pointer items-center justify-center rounded-[10px] shadow-[1px_5px_11px_0px_rgba(139,92,246,0.32)] transition-all hover:shadow-[0_0_25px_4px_rgba(139,92,246,0.24)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none disabled:ring-0 disabled:saturate-50 disabled:focus-visible:ring-gray-6',
    label: 'relative z-[1] whitespace-nowrap',
  },
  variants: {
    variant: {
      solid: {
        button: 'bg-[linear-gradient(248deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)] text-white',
        label: 'text-white',
      },
      soft: {
        button: '',
        label: 'text-gray-12',
      },
      ghost: {
        button: 'border-transparent shadow-none hover:shadow-none',
        label: 'text-gray-11',
      },
      'ai-outline': {
        button: 'rounded-lg border-transparent bg-white text-[#0248e9] shadow-[0_1px_2px_0_rgba(0,27,184,0.05)]',
        label: 'bg-[linear-gradient(90deg,#7c3aed_0%,#6d28d9_15%,#0248e9_65%,#327af6_100%)] bg-clip-text text-transparent',
      },
    },
    size: {
      xs: { button: 'h-7 gap-1 px-1.5 pr-2.5 py-1' },
      sm: { button: 'h-8 gap-1.5 px-1.5 pr-3 py-1.5' },
      md: { button: 'h-10 gap-2 px-2 pr-4 py-2' },
      lg: { button: 'h-11 gap-2 px-2.5 pr-5 py-2' },
      xl: { button: 'h-12 gap-2.5 px-3 pr-5 py-2.5' },
      '2xl': { button: 'h-14 gap-3 px-3.5 pr-6 py-3' },
    },
  },
  defaultVariants: {
    variant: 'solid' as AIButtonVariant,
    size: 'md' as AIButtonSize,
  },
});

type AIButtonCtaSlotProps = {
  ariaLabel: string;
  className?: string;
  dataTestId?: string;
  decorPaused: boolean;
  icon?: ReactNode;
  isDisabled: boolean;
  isHovered: boolean;
  isSelected: boolean;
  label: ReactNode;
  size: AIButtonSize;
  style?: CSSProperties;
  variant: AIButtonVariant;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AIButtonCtaSlot = forwardRef<HTMLButtonElement, AIButtonCtaSlotProps>(function AIButtonCtaSlot(
  { ariaLabel, className, dataTestId, decorPaused, icon, isDisabled, isHovered, isSelected, label, size, style, variant, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const isSolid = variant === 'solid';
  const { button, label: labelStyles } = aiButtonCtaStyles({ variant, size });
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(button(), className)}
      data-testid={dataTestId ?? 'ai-button-cta'}
      disabled={isDisabled}
      ref={ref}
      style={getVariantStyle(variant, style)}
      type='button'
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {icon ? (
        <span className='relative z-[1] inline-flex'>{icon}</span>
      ) : (
        <AIIcon animationActive={decorPaused ? false : undefined} isHovered={isHovered} size={CTA_ICON_SIZE_MAP[size]} sparklesPaint={isSolid ? 'white' : 'brand-gradient'} />
      )}
      <Typography as='span' className={labelStyles()} variant={CTA_LABEL_VARIANT[size]}>
        {label}
      </Typography>
    </button>
  );
});

AIButtonCtaSlot.displayName = 'AIButtonCtaSlot';

// ─── Icon-only (shape === 'rounded' | 'circle') ──────────────────────────────

const aiButtonIconStyles = tv({
  base: 'group relative inline-flex shrink-0 cursor-pointer items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none disabled:ring-0 disabled:saturate-50 disabled:focus-visible:ring-gray-6',
  variants: {
    variant: {
      solid:
        'bg-[linear-gradient(248deg,#8b5cf6_20%,#0248e9_60%,#001bb8_100%)] text-white shadow-[1px_5px_11px_0px_rgba(139,92,246,0.32)] hover:shadow-[0_0_25px_4px_rgba(139,92,246,0.24)]',
      soft: '',
      ghost: '',
      'ai-outline': 'border border-transparent bg-white text-[#0248e9] shadow-[0_1px_2px_0_rgba(0,27,184,0.05)] hover:shadow-[0_0_25px_4px_rgba(139,92,246,0.24)]',
    },
    size: {
      xs: 'size-6',
      sm: 'size-8',
      md: 'size-10',
      lg: 'size-12',
      xl: 'size-14',
      '2xl': 'size-16',
    },
    shape: {
      rounded: '',
      circle: 'rounded-full',
      cta: '',
    },
  },
  compoundVariants: [
    { size: 'xs', shape: 'rounded', class: 'rounded-md' },
    { size: 'sm', shape: 'rounded', class: 'rounded-lg' },
    { size: 'md', shape: 'rounded', class: 'rounded-[10px]' },
    { size: 'lg', shape: 'rounded', class: 'rounded-xl' },
    { size: 'xl', shape: 'rounded', class: 'rounded-xl' },
    { size: '2xl', shape: 'rounded', class: 'rounded-xl' },
  ],
  defaultVariants: {
    variant: 'solid' as AIButtonVariant,
    size: 'md' as AIButtonSize,
    shape: 'rounded' as AIButtonShape,
  },
});

type AIButtonIconSlotProps = {
  ariaLabel: string;
  className?: string;
  dataTestId?: string;
  decorPaused: boolean;
  icon?: ReactNode;
  isDisabled: boolean;
  isHovered: boolean;
  isSelected: boolean;
  shape: AIButtonShape;
  size: AIButtonSize;
  style?: CSSProperties;
  variant: AIButtonVariant;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

const AIButtonIconSlot = forwardRef<HTMLButtonElement, AIButtonIconSlotProps>(function AIButtonIconSlot(
  { ariaLabel, className, dataTestId, decorPaused, icon, isDisabled, isHovered, isSelected, shape, size, style, variant, onClick, onMouseEnter, onMouseLeave },
  ref
) {
  const useGradientSparkles = variant !== 'solid';
  return (
    <button
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      className={cn(aiButtonIconStyles({ variant, size, shape }), className)}
      data-testid={dataTestId ?? 'ai-button'}
      disabled={isDisabled}
      ref={ref}
      style={getVariantStyle(variant, style)}
      type='button'
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {icon ?? (
        <AIIcon
          animationActive={decorPaused ? false : undefined}
          isHovered={isHovered}
          size={ICON_SIZE_MAP[size]}
          sparklesPaint={useGradientSparkles ? 'brand-gradient' : 'white'}
        />
      )}
    </button>
  );
});

AIButtonIconSlot.displayName = 'AIButtonIconSlot';

// ─── AIButton ────────────────────────────────────────────────────────────────

const AIButton = forwardRef<HTMLButtonElement, AIButtonProps>(
  (
    {
      showTooltip = true,
      className,
      dataTestId,
      'data-testid': dataTestIdAttr,
      'aria-label': ariaLabelProp,
      disabled = false,
      size = 'md',
      style,
      variant = 'solid',
      shape = 'rounded',
      icon,
      label,
      onClick = () => {
        void 0;
      },
      isSelected = false,
    },
    ref
  ) => {
    const { t } = useTranslation('ai');
    const [isHovered, setIsHovered] = useState(false);

    const isDisabled = disabled;
    const decorPaused = isDisabled;

    const sharedSlotProps = {
      ref,
      className,
      dataTestId: dataTestIdAttr ?? dataTestId,
      decorPaused,
      icon,
      isDisabled,
      isHovered,
      isSelected,
      style,
      variant,
      onClick,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    };

    if (shape === 'cta') {
      const ariaLabel = ariaLabelProp ?? (typeof label === 'string' ? label : t('assistant_float.aria_label'));
      return (
        <Tooltip enable={showTooltip} content={t('assistant_float.label')} asChild>
          <AIButtonCtaSlot {...sharedSlotProps} ariaLabel={ariaLabel} label={label} size={size} />
        </Tooltip>
      );
    }

    return (
      <Tooltip enable={showTooltip} content={t('assistant_float.label')} asChild>
        <AIButtonIconSlot {...sharedSlotProps} ariaLabel={ariaLabelProp ?? t('assistant_float.aria_label')} shape={shape} size={size} />
      </Tooltip>
    );
  }
);

AIButton.displayName = 'AIButton';

export default AIButton;
